import { canonicalizeKeyedValue } from "@capture/shared";

/**
 * E-021 — per-field INPUT-format normalization at keying time.
 *
 * When an operator-admin sets a per-field `keyStandard` on a date field (e.g.
 * `MMDDYYYY` for separatorless 8-digit keying), the Verification Station
 * normalizes the operator's keyed value into its canonical form on commit so
 * the stored value is unambiguous regardless of how fast the operator typed.
 * Input format and export/output format stay decoupled — export-side
 * transforms render the canonical value however the destination wants.
 *
 * Reuses the shared `canonicalizeKeyedValue` (canonical/key-standard.ts) — the
 * single source of truth for date/decimal parsing. NO parallel parser here.
 *
 * Back-compat & fail-open:
 *   - No `keyStandard` set        → value passes through (job-type default
 *     handling stays exactly as before). Without this guard `canonicalizeKeyedValue`
 *     would canonicalize via its built-in default, changing current behavior.
 *   - Non-canonical data type     → `canonicalizeKeyedValue` returns null → pass through.
 *   - Empty / unparseable value   → `canonicalizeKeyedValue` returns null → pass
 *     through untouched (`?? value`), so the operator keeps their raw input and
 *     any validation rule can flag it rather than the field silently blanking.
 */
export function normalizeKeyedValue(
  value: string,
  field: { dataType: string; keyStandard?: string | null },
): string {
  if (!field.keyStandard) return value;
  return canonicalizeKeyedValue(value, field.dataType, field.keyStandard) ?? value;
}
